import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from './ui/button';

const Testimonials = () => {
  const { t, language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [googleStats, setGoogleStats] = useState({ rating: 5.0, review_count: 227 });
  
  const reviews = [
    {
      name: "Alexane Furioso",
      country: "France",
      rating: 5,
      text: "Super expérience avec cette agence de jetski ! Tout s'est parfaitement bien passé du début à la fin. L'équipe est vraiment sympa, professionnelle et aux petits soins.",
      date: "il y a 7 mois"
    },
    {
      name: "Emmanuel",
      country: "France",
      rating: 5,
      text: "Magnifique expérience! Nous sommes passées par Jeremy qui nous a accoster en centre ville et qui nous réserver un accueil très généreux nous avons pris 1 heure de jet ski et c'était parfait encore merci à vous",
      date: "il y a 6 mois"
    },
    {
      name: "Melanie Leur",
      country: "France",
      rating: 5,
      text: "Au top ! Super sympa. Le personnel est vraiment au petit soin. Nous avons eu la chance de voir les dauphins. Je conseille vivement.",
      date: "il y a 6 mois"
    },
    {
      name: "Jérémy Gossmann",
      country: "France",
      rating: 5,
      text: "Nous étions un groupe de 12 français et personnel super sympa et qui parle plusieurs langues dont le français ! Expérience au top, je recommande sans hésitation !",
      date: "il y a 7 mois"
    },
    {
      name: "Jake Thurston",
      country: "UK",
      rating: 5,
      text: "Unreal service, unreal experience and the quality of the actual units is amazing compared to others I've been with. I'm coming back and I'm 100% going to rebook. Thank you to the team!",
      date: "9 months ago"
    },
    {
      name: "Fiona Robertson",
      country: "UK",
      rating: 5,
      text: "Best jet skiing, staff super nice, 100% recommended. We have visited before with another company and this was by far the best, organised, most fun we have had. 5 stars, thank you!",
      date: "7 months ago"
    },
    {
      name: "Gerard Gaffney",
      country: "Ireland",
      rating: 5,
      text: "Fantastic day. Very professional and friendly. 30 minutes starts when out at sea unlike other companies. Even took us dolphin spotting after we finished. Highly recommend",
      date: "8 months ago"
    },
    {
      name: "Jade Haring",
      country: "Netherlands",
      rating: 5,
      text: "An unforgettable experience! The boat was in perfect condition — clean, spacious, and very comfortable. Captain Tarek and sailor Alejandro were absolutely amazing!",
      date: "8 months ago"
    },
    {
      name: "Marina Melero",
      country: "España",
      rating: 5,
      text: "Experiencia superdivertiva, hemos visto delfines y pasado una tarde fantástica. El trato de Alejandro inmejorable, la recomiendo 100%!!!",
      date: "hace 8 meses"
    },
    {
      name: "victor bris",
      country: "España",
      rating: 5,
      text: "Esta gente es profesional y humana. Gente enamorada de lo que hace. Del local al puerto nos han enseñado varias curiosidades. Este es el servicio que esta costa necesita!",
      date: "hace 6 meses"
    },
    {
      name: "Aeneas Z",
      country: "International",
      rating: 5,
      text: "We had an absolutely amazing & exciting JetSki experiencia with WOW Nautic this morning, Highend Jetski, great & speedy Motor!! And most importantly super friendly & professional services. Such great Fun!!!",
      date: "9 months ago"
    },
    {
      name: "Candi",
      country: "International",
      rating: 5,
      text: "Best experience and I would encourage everyone to live this experience. Great price and best guider/instructor. Loved every minute of it.",
      date: "4 months ago"
    }
  ];

  const itemsPerPage = 3;
  const totalPages = Math.ceil(reviews.length / itemsPerPage);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalPages);
    }, 5000);
    return () => clearInterval(interval);
  }, [totalPages]);

  useEffect(() => {
    // Fetch Google Reviews stats
    fetch('/api/google-reviews-stats')
      .then(res => res.json())
      .then(data => {
        setGoogleStats({
          rating: data.rating || 5.0,
          review_count: data.review_count || 227
        });
      })
      .catch(err => {
        console.error('Error fetching Google stats:', err);
      });
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const currentReviews = reviews.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  const getButtonText = () => {
    if (language === 'fr') return 'Laisser un avis';
    if (language === 'es') return 'Dejar una reseña';
    return 'Leave a review';
  };

  return (
    <section id="testimonials" className="py-20 bg-gray-50 border-y border-gray-100">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-6 h-6 text-yellow-500 fill-current" />
            ))}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-primary-950 mb-4">{t('sections.testimonialsTitle')}</h2>
          <p className="text-gray-600">
            {language === 'fr' && `Basé sur ${googleStats.review_count} avis sur Google`}
            {language === 'es' && `Basado en ${googleStats.review_count} reseñas en Google`}
            {language === 'en' && `Based on ${googleStats.review_count} reviews on Google`}
          </p>
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {currentReviews.map((review, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 relative">
                <Quote className="absolute top-6 right-6 w-8 h-8 text-primary-100" />
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-6 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
                  <div>
                    <h4 className="font-bold text-primary-950">{review.name}</h4>
                    <p className="text-xs text-gray-500">{review.country}</p>
                  </div>
                  <span className="text-xs text-gray-400">{review.date}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-4 mb-8">
            <button
              onClick={prevSlide}
              className="p-2 rounded-full bg-white border border-gray-200 hover:bg-primary-50 hover:border-primary-300 transition-colors"
              aria-label="Previous reviews"
            >
              <ChevronLeft className="w-6 h-6 text-primary-950" />
            </button>
            
            <div className="flex gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex ? 'bg-primary-600 w-8' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="p-2 rounded-full bg-white border border-gray-200 hover:bg-primary-50 hover:border-primary-300 transition-colors"
              aria-label="Next reviews"
            >
              <ChevronRight className="w-6 h-6 text-primary-950" />
            </button>
          </div>

          {/* Leave Review Button */}
          <div className="flex justify-center">
            <Button
              asChild
              className="bg-accent-500 hover:bg-accent-600 text-white font-semibold px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              <a
                href="https://admin.trustindex.io/api/googleWriteReview?place-id=ChIJO4tXqVzjcg0Rxgs1CeykDaE"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                {getButtonText()}
                <ExternalLink className="w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
